import React from 'react';
import { Brain, ListChecks, Swords, FileSearch, ShieldAlert, Ban, BookOpen, UserCheck, Loader2 } from 'lucide-react';


const Block = ({ icon: Icon, title, color, children, testid }) => (
  <div data-testid={testid} className="bg-slate-900/60 rounded-xl p-4 border border-slate-700">
    <p className={`text-sm font-semibold mb-3 flex items-center gap-2 ${color}`}>
      <Icon className="w-4 h-4" /> {title}
    </p>
    {children}
  </div>
);

const Bullets = ({ items }) => (
  <ul className="space-y-1.5">
    {(items || []).map((t, i) => (
      <li key={i} className="text-sm text-slate-300 flex gap-2"><span className="text-slate-500">•</span>{t}</li>
    ))}
  </ul>
);

export const StrategyPanel = ({ strategy, loading, actions, label = 'Estrategia de Victoria' }) => {
  if (loading) {
    return (
      <div data-testid="strategy-loading" className="mt-6 rounded-2xl border border-amber-500/30 bg-amber-500/5 p-6 flex items-center gap-3">
        <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />
        <p className="text-amber-200 text-sm">Cerebro está preparando tu informe (≈30-60 s)...</p>
      </div>
    );
  }
  if (!strategy) return null;
  return (
    <section data-testid="victory-strategy" className="mt-6 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-slate-900 p-5 md:p-6 space-y-4">
      <div className="flex flex-col md:flex-row md:items-start gap-4 justify-between">
        <div className="flex-1">
          <p className="text-xs font-semibold uppercase tracking-wider text-amber-400 flex items-center gap-2 mb-1">
            <Brain className="w-4 h-4" /> {label} · Cerebro IA
          </p>
          <p data-testid="strategy-summary" className="text-white text-base leading-relaxed">{strategy.resumen}</p>
        </div>
        <div className="text-left md:text-right shrink-0">
          <p data-testid="strategy-judges-count" className="text-xs text-slate-400">{strategy.jueces_analizados || 0} juez(es) analizados</p>
          <p className="text-[11px] text-slate-500">Sin porcentajes de éxito: la valoración del caso es cualitativa.</p>
          {actions && <div className="mt-2 flex md:justify-end gap-2">{actions}</div>}
        </div>
      </div>

      <Block icon={ListChecks} title="Plan paso a paso" color="text-amber-300" testid="strategy-plan">
        <ol className="space-y-3">
          {(strategy.plan || []).map((s, i) => (
            <li key={i} className="flex gap-3">
              <span className="w-6 h-6 shrink-0 rounded-full bg-amber-500 text-slate-950 text-xs font-bold flex items-center justify-center">{i + 1}</span>
              <div>
                <p className="text-white text-sm font-semibold">{s.paso}</p>
                <p className="text-slate-400 text-sm">{s.detalle}</p>
              </div>
            </li>
          ))}
        </ol>
      </Block>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Block icon={Swords} title="Argumentos clave" color="text-green-400" testid="strategy-arguments"><Bullets items={strategy.argumentos_clave} /></Block>
        <Block icon={FileSearch} title="Pruebas recomendadas" color="text-blue-400" testid="strategy-evidence"><Bullets items={strategy.pruebas_recomendadas} /></Block>
        <Block icon={ShieldAlert} title="Riesgos y cómo mitigarlos" color="text-red-400" testid="strategy-risks">
          <ul className="space-y-2">
            {(strategy.riesgos || []).map((r, i) => (
              <li key={i} className="text-sm"><p className="text-slate-200">{r.riesgo}</p><p className="text-slate-400">→ {r.mitigacion}</p></li>
            ))}
          </ul>
        </Block>
        <Block icon={Ban} title="Evitar" color="text-orange-400" testid="strategy-avoid"><Bullets items={strategy.evitar} /></Block>
        <Block icon={BookOpen} title="Normas aplicables" color="text-purple-400" testid="strategy-laws"><Bullets items={strategy.normas_aplicables} /></Block>
        <Block icon={UserCheck} title="Cómo adaptar el alegato a cada juez" color="text-cyan-400" testid="strategy-per-judge">
          <ul className="space-y-2">
            {(strategy.por_juez || []).map((j, i) => (
              <li key={i} className="text-sm"><span className="text-white font-medium">{j.nombre}: </span><span className="text-slate-400">{j.enfoque}</span></li>
            ))}
          </ul>
        </Block>
      </div>
      <p className="text-xs text-slate-500">Orientación estratégica generada por IA; no garantiza el resultado del juicio.</p>
    </section>
  );
};
